
import {EventBus} from "../../../../common/ts/events/EventBus";

import {SaveWebPushAlertRequestedEvent, WPNPermissionsEvent} from "../components/retention/webpush/events";
import eventHandlers from "../components/retention/webpush/eventHandlers";
import {TrovitSerpData} from "../TrovitPageData";
import {isDesktopDevice, isMobileDevice} from "../../device";
import {WebPushNotificationsStorage} from "./model/webPushNotificationsStorage";
import {
    UserHasNotInteractedEvent,
    GoogleOneTapCloseEvent, GoogleOneTapFlowEvent, PointOfCaptureExitEvent,
    WebPushNotificationDeniedEvent, WebPushNotificationGrantedEvent,
    WhitelabelEvent
} from "../../../../common/ts/events/Events";
import TrovitPathPrefixPreponder from "../../core/url/TrovitPathPrefixPreponder";
let eventBus: EventBus = EventBus.getInstance();
const wpnStorage = new WebPushNotificationsStorage();
const wpnTrackingConstants = {
    trackingPermissions: 'permissions',
    trackingEventRequest: 'request',
    trackingEventDisallow: 'disallow',
    trackingEventAllow: 'allow',
    trackingEventClose: 'close',
}

async function registerMobileServiceWorker(pageData: TrovitSerpData, flow: GoogleOneTapFlowEvent) {
    const serviceWorkerPath = TrovitPathPrefixPreponder.prependVerticalPrefixToPath('/service-worker.js');
    await navigator.serviceWorker.register(serviceWorkerPath);
    await checkPermissions(pageData, flow);
}

function registerMobileServiceWorkerByClose(pageData: TrovitSerpData) {
    return async (event: WhitelabelEvent) => {
        const {payload: {flow}} = (event as GoogleOneTapCloseEvent);
        await registerMobileServiceWorker(pageData, flow);
    };
}

async function registerDesktopServiceWorker(pageData: TrovitSerpData, flow: GoogleOneTapFlowEvent) {
    const serviceWorkerPath = TrovitPathPrefixPreponder.prependVerticalPrefixToPath('/service-worker.desktop.js');
    await navigator.serviceWorker.register(serviceWorkerPath);
    await checkPermissions(pageData, flow);
}

function registerDesktopServiceWorkerByClose(pageData: TrovitSerpData) {
    return async (event: WhitelabelEvent) => {
        const {payload: {flow}} = (event as GoogleOneTapCloseEvent);
        await registerDesktopServiceWorker(pageData, flow);
    }
}

async function init(pageData: TrovitSerpData) {
    if ('serviceWorker' in navigator) {
        eventHandlers.init();
        if (
            !isLocalhost() &&
            isDesktopDevice(pageData.device)
        ) {
            eventBus.subscribeWithId(
                GoogleOneTapCloseEvent.TYPE,
                {
                    id: `${GoogleOneTapCloseEvent.TYPE}-handler-wpn-desktop-close`,
                    callback: registerDesktopServiceWorkerByClose(pageData)
                }
            )
            eventBus.subscribeWithId(PointOfCaptureExitEvent.TYPE, {
                id: `${PointOfCaptureExitEvent.TYPE}-handler-wpn`,
                callback: async (event: WhitelabelEvent) => {
                    const {payload: {flow}} = (event as PointOfCaptureExitEvent);
                    if (flow == GoogleOneTapFlowEvent.SIGN_IN) {
                        await registerDesktopServiceWorker(pageData, flow);
                    }
                }
            });
            eventBus.subscribeWithId(UserHasNotInteractedEvent.TYPE, {
                id: `${UserHasNotInteractedEvent.TYPE}-handler-wpn`,
                callback: async (event: WhitelabelEvent) => {
                    const {payload: {flow}} = (event as UserHasNotInteractedEvent);
                    await registerDesktopServiceWorker(pageData, flow);
                }
            });
        } else if (
            !isLocalhost() &&
            isMobileDevice(pageData.device)
        ) {
            eventBus.subscribeWithId(
                GoogleOneTapCloseEvent.TYPE,
                {
                    id: `${GoogleOneTapCloseEvent.TYPE}-handler-wpn-mobile-close`,
                    callback: registerMobileServiceWorkerByClose(pageData)
                }
            )
            eventBus.subscribeWithId(PointOfCaptureExitEvent.TYPE, {
                id: `${PointOfCaptureExitEvent.TYPE}-handler-wpn`,
                callback: async (event: WhitelabelEvent) => {
                    const {payload: {flow}} = (event as PointOfCaptureExitEvent);
                    if (flow == GoogleOneTapFlowEvent.SIGN_IN) {
                        await registerMobileServiceWorker(pageData, flow);
                    }
                }
            });
            eventBus.subscribeWithId(UserHasNotInteractedEvent.TYPE, {
                id: `${UserHasNotInteractedEvent.TYPE}-handler-wpn`,
                callback: async (event: WhitelabelEvent) => {
                    const {payload: {flow}} = (event as UserHasNotInteractedEvent);
                    await registerMobileServiceWorker(pageData, flow);
                }
            });
        }
    }
}

async function checkPermissions(pageData: TrovitSerpData, flow: GoogleOneTapFlowEvent) {
    if (isBrowserCompatibleWithWPN()) {
        await tracking(wpnTrackingConstants.trackingEventRequest, pageData);
        await handlePermissions(pageData, flow);
    }
}

async function handlePermissions(pageData: TrovitSerpData, flow: GoogleOneTapFlowEvent) {
    if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission === 'default') {
            await tracking(wpnTrackingConstants.trackingEventClose, pageData);
        } else if (permission === 'granted') {
            await tracking(wpnTrackingConstants.trackingEventAllow, pageData);
            await checkSubscribeToWPN(pageData);
            emitWPNGranted(flow);
        } else {
            await tracking(wpnTrackingConstants.trackingEventDisallow, pageData);
            emitWPNDenied(flow);
        }
    } else if (Notification.permission === 'granted') {
        await checkSubscribeToWPN(pageData);
        emitWPNGranted(flow);
    } else if (Notification.permission === 'denied') {
        await tracking(wpnTrackingConstants.trackingEventDisallow,pageData);
        emitWPNDenied(flow);
    }
}

function emitWPNGranted(flow: GoogleOneTapFlowEvent) {
    eventBus.emit(new WebPushNotificationGrantedEvent({ flow: flow }));
}

function emitWPNDenied(flow: GoogleOneTapFlowEvent) {
    eventBus.emit(new WebPushNotificationDeniedEvent({ flow: flow }));
}

async function checkSubscribeToWPN(pageData: TrovitSerpData) {
    if (!isLocalhost()) {
        const swRegistration = await navigator.serviceWorker.ready;
        let subscription = await swRegistration.pushManager.getSubscription();
        if (!subscription) {
            await subscribeToWPN(pageData);
            return;
        }

        if (subscription && wpnStorage.getSearch(pageData.what) === undefined) {
            await updateWPNList(subscription, pageData);
        }
    } else {
        if (await wpnStorage.getSearch(pageData.what) === undefined) {
            wpnStorage.saveSearch(pageData.what);
        }
    }
}

async function updateWPNList(subscription: PushSubscription, pageData: TrovitSerpData) {
    eventBus.emit(new SaveWebPushAlertRequestedEvent({
        search: pageData,
        subscription
    }));
    wpnStorage.saveSearch(pageData.what);
}

async function tracking(event: String, pageData: TrovitSerpData){
    var userId = '';
    if (event == wpnTrackingConstants.trackingEventAllow && wpnStorage.getPushUserId() !== '') {
        userId = wpnStorage.getPushUserId();
    }
    eventBus.emit(new WPNPermissionsEvent({ pageData: pageData, userId: userId, eventType: event}));
}

async function subscribeToWPN(pageData: TrovitSerpData) {
    const swRegistration = await navigator.serviceWorker.ready;
    const subscription = await swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array('BJFfIhYJIx1qtxNbwycJl7RMks_lf1oB38U7u5d70LWdQ0aZHj75RGy_BCwg-ADPPaQoFFsKmadoDiIf-ATQPac'),
    });
    if (subscription && wpnStorage.getSearch(pageData.what) === undefined) {
        await updateWPNList(subscription, pageData);
    }
}

const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/\-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};

function isBrowserCompatibleWithWPN(): boolean {
    return 'PushManager' in window && 'showNotification' in ServiceWorkerRegistration.prototype;
}

function isLocalhost(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

export default {
    init,
};
